import React, { useState, useEffect, useCallback } from 'react';
import { Flag, Clock, User, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

// Use relative URLs so the call always goes to the same origin as the page.
// The rest of the codebase follows this pattern — it avoids build-time
// REACT_APP_BACKEND_URL baking and keeps auth cookies on the same origin.
async function _fetch(path, opts = {}) {
  const res = await fetch(`/api${path}`, { credentials: 'include', ...opts });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json();
}

export function FeatureFlagsPanel() {
  const [state, setState] = useState({ environment: 'dev', flags: [] });
  const [audit, setAudit] = useState({}); // { flagName: [entries] }
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [reasonModal, setReasonModal] = useState(null); // { flagName, nextValue }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await _fetch('/admin/feature-flags');
      setState(data);
      // Load audit for each flag
      const auditMap = {};
      await Promise.all((data.flags || []).map(async (f) => {
        try {
          const a = await _fetch(`/admin/feature-flags/${f.name}/audit?limit=10`);
          auditMap[f.name] = a.entries || [];
        } catch { auditMap[f.name] = []; }
      }));
      setAudit(auditMap);
    } catch (err) {
      toast.error(`Failed to load flags: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyToggle = async (flagName, nextValue, reason) => {
    setTogglingId(flagName);
    try {
      await _fetch(`/admin/feature-flags/${flagName}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: nextValue, reason }),
      });
      toast.success(`${flagName} set to ${nextValue}`);
      setReasonModal(null);
      await load();
    } catch (err) {
      toast.error(`Toggle failed: ${err.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="msml-card" data-testid="feature-flags-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flag className="w-5 h-5 text-[var(--msml-primary)]" />
          <h3 className="text-lg font-medium text-white">Feature Flags</h3>
          <span className="px-2 py-0.5 rounded-full bg-[var(--msml-primary-muted)] text-[var(--msml-primary)] text-xs font-mono uppercase">
            {state.environment}
          </span>
        </div>
        <Button
          variant="ghost" size="sm" onClick={load}
          className="text-[var(--msml-text-muted)]"
          data-testid="feature-flags-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>
      <p className="text-sm text-[var(--msml-text-muted)] mb-4">
        Runtime toggles. Every change is audit-logged (who, when, old → new, environment).
        Flag state is embedded into every validation run's runtime signature.
      </p>

      {loading ? (
        <div className="flex justify-center py-8"><span className="msml-spinner" /></div>
      ) : (
        <div className="space-y-4">
          {state.flags.map((f) => (
            <div key={f.name} className="border border-white/10 rounded-lg p-4" data-testid={`flag-card-${f.name}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-sm text-white font-mono">{f.name}</code>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      f.value ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'
                    }`}>{f.value ? 'ON' : 'OFF'}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-white/5 text-[var(--msml-text-muted)]">
                      source: {f.source}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--msml-text-muted)] leading-relaxed">{f.description}</p>
                  {f.last_changed_by && (
                    <div className="mt-2 flex items-center gap-3 text-xs text-[var(--msml-text-muted)]">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {f.last_changed_by}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(f.last_changed_at)}</span>
                      <span>prev: <code>{String(f.previous_value)}</code></span>
                    </div>
                  )}
                </div>
                <Switch
                  checked={f.value}
                  disabled={togglingId === f.name}
                  onCheckedChange={(next) => setReasonModal({ flagName: f.name, nextValue: next, currentValue: f.value })}
                  data-testid={`flag-toggle-${f.name}`}
                />
              </div>

              {(audit[f.name] || []).length > 0 && (
                <details className="mt-3 group">
                  <summary className="cursor-pointer text-xs text-[var(--msml-primary)] hover:opacity-80"
                           data-testid={`flag-audit-toggle-${f.name}`}>
                    Audit trail ({audit[f.name].length})
                  </summary>
                  <div className="mt-2 space-y-1 max-h-56 overflow-y-auto">
                    {audit[f.name].map((e, i) => (
                      <div key={i} className="text-xs bg-black/30 rounded px-3 py-2 border border-white/5">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[var(--msml-text-secondary)]">
                            {String(e.old_value)} → <strong className="text-white">{String(e.new_value)}</strong>
                          </span>
                          <span className="text-[var(--msml-text-muted)]">{formatDate(e.timestamp)}</span>
                        </div>
                        <div className="mt-1 text-[var(--msml-text-muted)]">
                          by {e.user_email} · env: {e.environment}
                          {e.reason && <> · reason: <em>{e.reason}</em></>}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {reasonModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
             data-testid="flag-reason-modal">
          <div className="msml-card max-w-md w-full">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-[var(--msml-warn)] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-base font-medium text-white">Confirm flag change</h4>
                <p className="text-xs text-[var(--msml-text-muted)] mt-1">
                  <code className="text-white">{reasonModal.flagName}</code>: {String(reasonModal.currentValue)} → {String(reasonModal.nextValue)}
                </p>
              </div>
            </div>
            <label className="block text-xs text-[var(--msml-text-muted)] mb-1">
              Reason (recorded in audit log)
            </label>
            <textarea
              className="msml-input w-full min-h-[80px] text-sm"
              placeholder="e.g. Enabling for demo with reviewer / disabling for A/B test"
              id="flag-reason-input"
              data-testid="flag-reason-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setReasonModal(null)}
                      data-testid="flag-reason-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const reason = document.getElementById('flag-reason-input').value;
                  applyToggle(reasonModal.flagName, reasonModal.nextValue, reason || null);
                }}
                data-testid="flag-reason-confirm"
              >
                Confirm change
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
